import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getAutoDetectionInfo, LANGUAGES } from "@/i18n";
import { Globe } from "lucide-react";

/**
 * LanguageDetectionNotifier
 * Shows a friendly toast notification when the user's language is auto-detected
 * on their first visit based on browser settings.
 */
const LanguageDetectionNotifier = () => {
  const { i18n } = useTranslation();
  const hasShownRef = useRef(false);

  useEffect(() => {
    // Only show once per session
    if (hasShownRef.current) return;
    
    const info = getAutoDetectionInfo();
    
    // Only show notification if:
    // 1. It's a first visit (no saved preference)
    // 2. A non-English language was auto-detected
    if (info.wasAutoDetected && info.detectedLanguage !== "en") {
      hasShownRef.current = true;
      
      const language = LANGUAGES.find(l => l.code === info.detectedLanguage);
      
      // Delay slightly to ensure page has rendered
      setTimeout(() => {
        toast.success(
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-purple-500" />
            <div>
              <p className="font-medium">
                {getWelcomeMessage(info.detectedLanguage)}
              </p>
              <p className="text-sm text-gray-500">
                {getSubMessage(info.detectedLanguage, language?.nativeName || language?.name)}
              </p>
            </div>
          </div>,
          {
            duration: 5000,
            action: {
              label: getSwitchToEnglishLabel(info.detectedLanguage),
              onClick: () => {
                i18n.changeLanguage("en");
                toast.info("Switched to English");
              }
            }
          }
        );
      }, 1500);
    }
  }, [i18n]);

  return null;
};

// Welcome messages in each supported language
const getWelcomeMessage = (langCode) => {
  const messages = {
    es: "¡Bienvenido!",
    fr: "Bienvenue !",
    pt: "Bem-vindo!",
    sw: "Karibu!",
    hi: "स्वागत है!",
    zh: "欢迎！",
    ru: "Добро пожаловать!",
    ig: "Nnọọ!"
  };
  return messages[langCode] || "Welcome!";
};

// Sub-message explaining auto-detection
const getSubMessage = (langCode, languageName) => {
  const messages = {
    es: `Detectamos ${languageName} como tu idioma`,
    fr: `Nous avons détecté ${languageName} comme votre langue`,
    pt: `Detectamos ${languageName} como seu idioma`,
    sw: `Tumegundua ${languageName} kama lugha yako`,
    hi: `हमने ${languageName} को आपकी भाषा के रूप में पहचाना`,
    zh: `我们检测到您使用${languageName}`,
    ru: `Мы определили ${languageName} как ваш язык`,
    ig: `Anyị chọpụtara ${languageName} dịka asụsụ gị`
  };
  return messages[langCode] || `We detected ${languageName} as your language`;
};

// "Switch to English" label in each language
const getSwitchToEnglishLabel = (langCode) => {
  const labels = {
    es: "Cambiar a inglés",
    fr: "Passer à l'anglais",
    pt: "Mudar para inglês",
    sw: "Badili kwa Kiingereza",
    hi: "अंग्रेजी में बदलें",
    zh: "切换到英文",
    ru: "На английский",
    ig: "Gbanwee gaa Bekee"
  };
  return labels[langCode] || "Switch to English";
};

export default LanguageDetectionNotifier;
